import { toApiStatus, toUiStatus } from './_helpers';
import { equipmentFromApi } from './equipment.model';
import { userEquipmentFromApi } from './userEquipment.model';
import { profilePhotoOnly } from '../utils/profilePhoto';

/** API IndentRequest (+ lines) ↔ UI fields. */

function toDateOnly(value) {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : String(value);
}

export function indentStatusFromApi(value) {
  if (value == null || value === '') return '';
  const raw = String(value).toLowerCase();
  if (raw === 'in_progress') return 'In progress';
  if (raw === 'cancelled') return 'Withdrawn';
  return toUiStatus(raw);
}

export function indentStatusToApi(value) {
  if (value == null || value === '') return value;
  const raw = String(value).toLowerCase().replace(/\s+/g, '_');
  if (raw === 'in_progress') return 'in_progress';
  if (raw === 'withdrawn' || raw === 'cancelled') return 'cancelled';
  return toApiStatus(value);
}

function equipmentTypeName(row) {
  const type = row?.equipmentType;
  if (!type) return '';
  if (typeof type === 'string') return type;
  return type.name || type.code || '';
}

function personLabel(user) {
  if (!user) return '';
  return (
    user.tradeName ||
    user.fullName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
    ''
  );
}

function appUserRoleLabel(appUser) {
  const type = appUser?.userType;
  if (type === 'agency') return 'Agency';
  if (type === 'service_provider') return 'Service provider';
  if (appUser) return 'Service provider';
  return '';
}

function recommendationFromApi(row) {
  if (!row) return null;
  const equipment = row.equipment ? equipmentFromApi(row.equipment) : null;
  const userEquipment = row.userEquipment
    ? userEquipmentFromApi(row.userEquipment)
    : null;
  const targetType =
    row.targetType ||
    (row.userEquipmentId ? 'user_equipment' : row.equipmentId ? 'equipment' : '');
  const appUser = row.appUser || null;
  const vendor = row.vendor || null;
  const villager = row.villager || null;

  const personName =
    personLabel(appUser) ||
    vendor?.tradeName ||
    villager?.fullName ||
    villager?.name ||
    '';
  const phone =
    appUser?.phone || vendor?.mobile || villager?.mobile || '';
  const village =
    appUser?.village || villager?.village?.name || villager?.villageName || '';

  const fleetAssetId =
    userEquipment?.assetId ||
    (row.userEquipmentId != null ? `UE-${row.userEquipmentId}` : '');

  return {
    id: row.id,
    recId: row.id != null ? `REC-${row.id}` : '',
    targetType,
    assetId: fleetAssetId || equipment?.assetId || row.equipment?.code || '',
    assetDbId:
      row.userEquipmentId ??
      userEquipment?.id ??
      row.equipmentId ??
      equipment?.id ??
      null,
    equipmentId: row.equipmentId ?? null,
    userEquipmentId: row.userEquipmentId ?? userEquipment?.id ?? null,
    appUserId: row.appUserId ?? appUser?.id ?? null,
    vendorId: row.vendorId ?? vendor?.id ?? null,
    villagerId: row.villagerId ?? villager?.id ?? null,
    personId: row.appUserId ?? row.vendorId ?? row.villagerId ?? null,
    personName,
    phone,
    village,
    roleLabel:
      targetType === 'app_user' || appUser
        ? appUserRoleLabel(appUser)
        : targetType === 'vendor'
          ? 'Vendor'
          : targetType === 'villager'
            ? 'Villager'
            : '',
    tradeName: appUser?.tradeName || vendor?.tradeName || '',
    profilePhotoUrl: profilePhotoOnly(appUser?.profilePhotoUrl),
    aadhaarFrontUrl: '',
    recommendedAt: row.recommendedAt ?? row.createdAt ?? null,
    recommendedBy: row.recommendedByStaff?.fullName || '',
    note: row.note ?? '',
    remark: row.remark ?? '',
    referredByAppUserId:
      row.referredByAppUserId ?? row.referredByAppUser?.id ?? null,
    referredByName:
      row.referredByName ||
      row.referredByAppUser?.fullName ||
      [
        row.referredByAppUser?.firstName,
        row.referredByAppUser?.lastName,
      ]
        .filter(Boolean)
        .join(' ')
        .trim() ||
      '',
    referredByLinked: Boolean(
      row.referredByAppUserId ?? row.referredByAppUser?.id,
    ),
    declineReason: row.declineReason ?? '',
    declinedAt: row.declinedAt ?? null,
    rerequestedAt: row.rerequestedAt ?? null,
    complianceAlerts: Array.isArray(row.complianceAlerts)
      ? row.complianceAlerts
      : [],
    equipmentDetails:
      row.equipmentDetails && typeof row.equipmentDetails === 'object'
        ? row.equipmentDetails
        : null,
    photoUrl: row.photoUrl || userEquipment?.thumbnailUrl || null,
    photoUrls: Array.isArray(row.photoUrls) ? row.photoUrls : [],
    status: indentStatusFromApi(row.status) || toUiStatus(row.status),
    equipment,
    userEquipment,
    equipmentName:
      userEquipment?.equipmentDescription ||
      userEquipment?.equipmentType ||
      equipment?.equipmentDescription ||
      equipment?.equipmentType ||
      '',
    equipmentType:
      userEquipment?.equipmentType || equipment?.equipmentType || '',
    assetName:
      userEquipment?.equipmentDescription ||
      userEquipment?.equipmentType ||
      equipment?.equipmentDescription ||
      equipment?.equipmentType ||
      personName ||
      '',
    assetCode: fleetAssetId || equipment?.assetId || '',
    ownerName: userEquipment
      ? userEquipment.ownerName || ''
      : equipment
        ? equipment.villagerName ||
          equipment.vendorName ||
          equipment.nameAsPerRc ||
          ''
        : personName,
    ownerPhone:
      userEquipment?.ownerPhone || equipment?.ownerMobile || phone || '',
    regNo: userEquipment?.regNo || equipment?.regNo || '',
    chassisNo: userEquipment?.chassisNumber || equipment?.chassisNo || '',
    manufacturer: userEquipment?.manufacturer || equipment?.manufacturer || '',
    model: userEquipment?.model || equipment?.model || '',
  };
}

function assignmentFromApi(row) {
  if (!row) return null;
  const equipment = row.equipment ? equipmentFromApi(row.equipment) : null;
  const userEquipment = row.userEquipment
    ? userEquipmentFromApi(row.userEquipment)
    : null;
  const actor = row.assignedByActor || '';
  /** Who performed Give work / Assign — for admin display. */
  const assignedBySource =
    actor === 'agency'
      ? 'Requester'
      : actor === 'staff'
        ? 'Team'
        : actor === 'vendor_portal'
          ? 'Vendor'
          : '';
  const assignedByActor = assignedBySource;
  const appUser = row.appUser || null;
  const vendor = row.vendor || null;
  const villager = row.villager || null;
  const personName =
    personLabel(appUser) ||
    vendor?.tradeName ||
    villager?.fullName ||
    villager?.name ||
    '';
  const fleetAssetId =
    userEquipment?.assetId ||
    (row.userEquipmentId != null ? `UE-${row.userEquipmentId}` : '');

  const assignedByName =
    row.assignedByLabel ||
    row.assignedByStaff?.fullName ||
    '';

  return {
    id: row.id,
    targetType: row.targetType || '',
    recommendationId: row.recommendationId ?? null,
    assetId:
      fleetAssetId ||
      equipment?.assetId ||
      row.equipment?.code ||
      row.equipmentId ||
      '',
    assetDbId:
      row.userEquipmentId ??
      userEquipment?.id ??
      row.equipmentId ??
      equipment?.id ??
      null,
    equipmentId: row.equipmentId ?? null,
    userEquipmentId: row.userEquipmentId ?? userEquipment?.id ?? null,
    appUserId: row.appUserId ?? appUser?.id ?? null,
    vendorId: row.vendorId ?? vendor?.id ?? null,
    villagerId: row.villagerId ?? villager?.id ?? null,
    personName,
    assignedAt: row.assignedAt ?? row.createdAt ?? null,
    assignedByActor,
    assignedByActorRaw: actor || '',
    assignedBySource,
    assignedByName,
    assignedBy: assignedByName,
    /** e.g. "Given by requester · Dee Pinara" / "Assigned by team · Aakash" */
    assignedByDisplay: (() => {
      if (actor === 'agency') {
        return assignedByName
          ? `Given by requester · ${assignedByName}`
          : 'Given by requester';
      }
      if (actor === 'staff') {
        return assignedByName
          ? `Assigned by team · ${assignedByName}`
          : 'Assigned by team';
      }
      if (actor === 'vendor_portal') {
        return assignedByName
          ? `Given by vendor · ${assignedByName}`
          : 'Given by vendor';
      }
      return assignedByName ? `by ${assignedByName}` : '';
    })(),
    releasedAt: row.releasedAt ?? null,
    releasedBy:
      row.releasedByLabel ||
      row.releasedByStaff?.fullName ||
      row.releasedBy ||
      '',
    releasedByActor: row.releasedByActor || '',
    note: row.note ?? '',
    poNumber: row.poNumber ?? '',
    poValue: row.poValue != null && row.poValue !== '' ? Number(row.poValue) : null,
    orderValue:
      row.orderValue != null && row.orderValue !== ''
        ? Number(row.orderValue)
        : null,
    releaseNote: row.releaseNote ?? '',
    releaseRating: row.releaseRating != null ? Number(row.releaseRating) : null,
    status: indentStatusFromApi(row.status),
    equipment,
  };
}

function lineFromApi(row) {
  if (!row) return null;
  const typeName = equipmentTypeName(row) || row.catalogLabel || '';
  const recommendations = (row.recommendations || []).map(recommendationFromApi);
  const rawAssignment =
    row.assignment ||
    (Array.isArray(row.assignments)
      ? row.assignments.find((a) => a && !a.releasedAt) || row.assignments[0]
      : null);
  const assignment = assignmentFromApi(rawAssignment);

  return {
    id: row.id,
    lineId: row.id,
    lineNo: row.lineNo ?? 0,
    lineType: row.lineType || 'equipment',
    buId: row.businessUnit?.code || '',
    buDbId: row.buId ?? row.businessUnit?.id ?? null,
    buName:
      row.businessUnit?.shortName ||
      row.businessUnit?.code ||
      '',
    siteId: row.site?.code || '',
    siteDbId: row.siteId ?? row.site?.id ?? null,
    siteName: row.site?.siteName || '',
    equipmentTypeId: row.equipmentTypeId ?? row.equipmentType?.id ?? null,
    catalogId: row.catalogId ?? null,
    catalogLabel: row.catalogLabel ?? typeName,
    equipmentType: typeName,
    specification: row.specification ?? '',
    quantity: row.quantity ?? 1,
    uom: row.uom ?? '',
    capacity: row.capacity ?? '',
    capacityUom: row.capacityUom ?? '',
    requiredFrom: toDateOnly(row.requiredFrom),
    requiredTill: toDateOnly(row.requiredTill),
    timePeriod: row.timePeriod ?? '',
    workingHours: row.workingHours ?? '',
    remarks: row.remarks ?? '',
    status: indentStatusFromApi(row.status),
    recommendations,
    assignment,
  };
}

function requesterFields(request) {
  const appUser = request.appUser || null;
  const isAgencyRequest = Boolean(request.appUserId || appUser?.id);
  const agencyName =
    personLabel(appUser) ||
    request.raisedByName ||
    '';
  const vendorCode = request.vendor?.code || '';
  const agencyCode = isAgencyRequest
    ? `AG-${request.appUserId || appUser?.id || ''}`
    : '';

  return {
    vendorId: vendorCode || agencyCode,
    vendorDbId: request.vendorId ?? request.vendor?.id ?? null,
    appUserId: request.appUserId ?? appUser?.id ?? null,
    requesterSource: isAgencyRequest ? 'agency' : 'vendor',
    requesterName: request.raisedByName || agencyName || '',
    vendorName:
      request.vendor?.tradeName ||
      request.vendor?.tradeAgencyName ||
      agencyName ||
      '',
    agencyTradeName: appUser?.tradeName || request.vendor?.tradeName || '',
    vendorLocalProfessional: request.vendor?.localProfessional || '',
  };
}

/** List row or bare IndentRequest. */
function indentListRowFromApi(row) {
  const request = row.indent || row;
  const requester = requesterFields(request);
  const requestNo =
    request.requestNo ||
    (request.id != null ? `REQ-${request.id}` : '');

  return {
    id: request.id,
    requestNo,
    indentId: requestNo,
    ...requester,
    raisedByName: request.raisedByName ?? '',
    raisedByEmail: request.raisedByEmail ?? '',
    createdAt: request.createdAt ?? null,
    requestedAt: request.createdAt ?? null,
    status: indentStatusFromApi(request.status),
    notes: request.notes ?? '',
    itemCount: Number(request.itemCount ?? 0),
    summary: request.summary ?? '',
    lineStatuses: Array.isArray(request.lineStatuses) ? request.lineStatuses : [],
    recommendationCount: Number(request.recommendationCount ?? 0),
    assignmentSummary: request.assignmentSummary || row.assignmentSummary || null,
    lines: Array.isArray(request.lines)
      ? request.lines.map(lineFromApi)
      : [],
    // Compat: first line as `line` for older create-form paths
    line: Array.isArray(request.lines) && request.lines[0]
      ? lineFromApi(request.lines[0])
      : {
          lineId: '',
          equipmentType: '',
          specification: '',
          quantity: 1,
          uom: '',
          requiredFrom: '',
          requiredTill: '',
        },
    recommendations: [],
    assignment: null,
    active: request.isActive !== false,
    updatedAt: request.updatedAt ?? null,
  };
}

function indentDetailFromApi(payload) {
  const request = payload.request || {};
  const lines = (payload.lines || []).map(lineFromApi);
  const base = indentListRowFromApi({
    ...request,
    itemCount: lines.length,
    summary:
      request.summary ||
      lines.map((l) => l.catalogLabel || l.equipmentType).filter(Boolean).join(', '),
  });

  return {
    ...base,
    lines,
    line: lines[0] || base.line,
    recommendationCount: lines.reduce(
      (n, l) => n + (l.recommendations?.length || 0),
      0,
    ),
  };
}

/**
 * Accepts:
 * - list row (IndentRequest + summaries)
 * - detail `{ request, lines }`
 * - bare create response (IndentRequest)
 */
export function indentFromApi(row) {
  if (!row) return null;
  if (row.request && Array.isArray(row.lines)) {
    return indentDetailFromApi(row);
  }
  return indentListRowFromApi(row);
}

export function indentToApi(ui) {
  if (!ui) return null;

  if (Array.isArray(ui.lines) && ui.lines.length > 0) {
    return {
      vendorId: ui.vendorDbId ?? ui.vendorId,
      raisedByName: ui.raisedByName || null,
      raisedByEmail: ui.raisedByEmail || null,
      notes: ui.notes || null,
      lines: ui.lines.map((line) => ({
        buId: line.buDbId ?? line.buId,
        siteId: line.siteDbId ?? line.siteId ?? null,
        lineType: line.lineType || 'equipment',
        equipmentTypeId: line.equipmentTypeId ?? null,
        catalogId: line.catalogId ?? null,
        catalogLabel: line.catalogLabel || line.equipmentType || null,
        specification: line.specification || null,
        quantity: line.quantity ?? 1,
        uom: line.uom || null,
        capacity: line.capacity || null,
        capacityUom: line.capacityUom || null,
        requiredFrom: line.requiredFrom,
        requiredTill: line.requiredTill,
        timePeriod: line.timePeriod || null,
        workingHours: line.workingHours || null,
        remarks: line.remarks || null,
      })),
    };
  }

  // Legacy single-line create (still accepted by backend)
  const line = ui.line || {};
  return {
    vendorId: ui.vendorDbId ?? ui.vendorId,
    buId: ui.buDbId ?? ui.buId,
    siteId: ui.siteDbId ?? ui.siteId,
    workArea: ui.workArea || null,
    raisedByName: ui.raisedByName || null,
    raisedByEmail: ui.raisedByEmail || null,
    notes: ui.notes || null,
    equipmentTypeId: line.equipmentTypeId,
    specification: line.specification || null,
    quantity: line.quantity ?? 1,
    uom: line.uom || null,
    capacity: line.capacity || null,
    capacityUom: line.capacityUom || null,
    requiredFrom: line.requiredFrom,
    requiredTill: line.requiredTill,
    timePeriod: line.timePeriod || null,
    workingHours: line.workingHours || null,
    remarks: line.remarks || null,
  };
}
